<template>
  <div class="price-comparison">
    <!-- 标题区域 -->
    <header class="header-section">
      <h1 class="main-title">香港乾元有限公司</h1>
      <p class="subtitle">每个版本均包含全系统功能与客户服务，有效提升运营管理效率</p>
    </header>

    <!-- 价格表格 -->
    <div class="pricing-table-container">
      <table class="pricing-table">
        <!-- 表头 -->
        <thead>
          <tr class="header-row">
            <th class="corner-cell"></th>
            <!-- 免费版 -->
            <th class="plan-header free-plan">
              <div class="plan-title">免费版</div>
              <a href="https://erp.qianyuanhk.com/" 
                 target="_blank" 
                 rel="nofollow"
                 class="btn btn-outline">
                免费使用
              </a>
            </th>
            <!-- 包年版 - 包含所有套餐列 -->
            <th :colspan="pricingTiers.length" class="plan-header yearly-plan">
              <div class="plan-title highlighted">包年版</div>
              <a href="https://erp.qianyuanhk.com/" 
                 target="_blank" 
                 rel="nofollow"
                 class="btn btn-primary">
                立即购买
              </a>
            </th>
          </tr>
          
          <!-- 第二行 - 套餐详情 -->
          <tr class="tier-row">
            <th class="section-label">价格</th>
            <!-- 免费版价格（占位） -->
            <td class="tier-cell free-tier">
              <div class="tier-quota">5000单/月</div>
              <div class="tier-price">¥0</div>
            </td>
            <!-- 包年版各套餐（顺序：12万→30万→50万→70万→100万→130万→200万→280万→350万→600万→900万） -->
            <td v-for="tier in pricingTiers" :key="tier.orders" class="tier-cell">
              <div class="tier-quota highlighted">{{ tier.orders }}</div>
              <div class="tier-price">¥{{ tier.price.toLocaleString() }}</div>
              <div class="tier-unit">¥{{ tier.unitPrice }}/单</div>
            </td>
          </tr>
        </thead>

        <!-- 表体 -->
        <tbody>
          <!-- 核心：功能行 + 对应展开行 -->
          <template v-for="(feature, index) in features" :key="feature.name">
            <!-- 功能行 -->
            <tr class="feature-row">
              <td class="feature-label">
                <div class="feature-name-wrapper">
                  <span>{{ feature.name }}</span>
                  <button v-if="feature.expandable" 
                          class="expand-btn"
                          @click="toggleExpand(index)">
                    <span class="arrow" :class="{ expanded: expandedRows.includes(index) }">›</span>
                  </button>
                </div>
              </td>
              
              <!-- 免费版 -->
              <td class="feature-cell free-cell">
                <template v-if="feature.freeValue === true">
                  <svg class="check-icon" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </template>
                <template v-else-if="feature.freeValue === false">
                  <span class="empty"></span>
                </template>
                <template v-else>
                  <span class="text-value">{{ feature.freeValue }}</span>
                </template>
              </td>
              
              <!-- 包年版（跨所有套餐列） -->
              <td :colspan="pricingTiers.length" class="feature-cell paid-cell">
                <template v-if="feature.paidValue === true">
                  <svg class="check-icon" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </template>
                <template v-else>
                  <span class="text-value">{{ feature.paidValue }}</span>
                </template>
              </td>
            </tr>

            <!-- 展开行：功能用量 -->
            <tr v-if="feature.name === '功能用量' && expandedRows.includes(index)" class="expand-row">
              <td :colspan="pricingTiers.length + 2" class="expand-cell">
                <div class="expand-content">
                  <table class="sub-pricing-table">
                    <thead>
                      <tr>
                        <th class="sub-feature-name">子功能</th>
                        <th class="sub-free-col">免费版</th>
                        <th v-for="tier in pricingTiers" :key="tier.orders" class="sub-paid-col">{{ tier.orders }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="sub in subFeatures" :key="sub.name" class="sub-feature-row">
                        <td class="sub-feature-label">{{ sub.name }}</td>
                        <td class="sub-feature-value">{{ sub.freeValue }}</td>
                        <td v-for="(val, idx) in sub.paidValues" :key="idx" class="sub-feature-value">{{ val || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>

            <!-- 展开行：运营工具 -->
            <tr v-if="feature.name === '运营工具' && expandedRows.includes(index)" class="expand-row">
              <td :colspan="pricingTiers.length + 2" class="expand-cell">
                <div class="expand-content">
                  <table class="sub-pricing-table">
                    <tbody>
                      <tr v-for="sub in subFeaturesOperations" :key="sub.name" class="sub-feature-row">
                        <td class="sub-feature-label">{{ sub.name }}</td>
                        <!-- 免费版列 -->
                        <td class="sub-feature-value">
                          <svg class="check-icon" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </td>
                        <!-- 包年版各套餐列 -->
                        <td v-for="(val, idx) in sub.paidValues" :key="idx" class="sub-feature-value">
                          <template v-if="val">
                            <svg class="check-icon" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                          </template>
                          <template v-else>
                            <span class="empty"></span>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>

            <!-- 展开行：财务管理 -->
            <tr v-if="feature.name === '财务管理' && expandedRows.includes(index)" class="expand-row">
              <td :colspan="pricingTiers.length + 2" class="expand-cell">
                <div class="expand-content">
                  <table class="sub-pricing-table">
                    <tbody>
                      <tr v-for="sub in subFeaturesFinance" :key="sub.name" class="sub-feature-row">
                        <td class="sub-feature-label">{{ sub.name }}</td>
                        <!-- 免费版列 -->
                        <td class="sub-feature-value">
                          <svg class="check-icon" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </td>
                        <!-- 包年版各套餐列 -->
                        <td v-for="(val, idx) in sub.paidValues" :key="idx" class="sub-feature-value">
                          <template v-if="val">
                            <svg class="check-icon" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                          </template>
                          <template v-else>
                            <span class="empty"></span>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>

            <!-- 展开行：进销存管理 -->
            <tr v-if="feature.name === '进销存管理' && expandedRows.includes(index)" class="expand-row">
              <td :colspan="pricingTiers.length + 2" class="expand-cell">
                <div class="expand-content">
                  <table class="sub-pricing-table">
                    <tbody>
                      <tr v-for="sub in subFeaturesInventory" :key="sub.name" class="sub-feature-row">
                        <td class="sub-feature-label">{{ sub.name }}</td>
                        <!-- 免费版列 -->
                        <td class="sub-feature-value">
                          <svg class="check-icon" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </td>
                        <!-- 包年版各套餐列 -->
                        <td v-for="(val, idx) in sub.paidValues" :key="idx" class="sub-feature-value">
                          <template v-if="val">
                            <svg class="check-icon" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                          </template>
                          <template v-else>
                            <span class="empty"></span>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>

            <!-- 展开行：数据管理 -->
            <tr v-if="feature.name === '数据管理' && expandedRows.includes(index)" class="expand-row">
              <td :colspan="pricingTiers.length + 2" class="expand-cell">
                <div class="expand-content">
                  <table class="sub-pricing-table">
                    <tbody>
                      <tr v-for="sub in subFeaturesData" :key="sub.name" class="sub-feature-row">
                        <td class="sub-feature-label">{{ sub.name }}</td>
                        <!-- 免费版列 -->
                        <td class="sub-feature-value">
                          <svg class="check-icon" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </td>
                        <!-- 包年版各套餐列 -->
                        <td v-for="(val, idx) in sub.paidValues" :key="idx" class="sub-feature-value">
                          <template v-if="val">
                            <svg class="check-icon" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                          </template>
                          <template v-else>
                            <span class="empty"></span>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>

            <!-- 展开行：广告管理 -->
            <tr v-if="feature.name === '广告管理' && expandedRows.includes(index)" class="expand-row">
              <td :colspan="pricingTiers.length + 2" class="expand-cell">
                <div class="expand-content">
                  <table class="sub-pricing-table">
                    <tbody>
                      <tr v-for="sub in subFeaturesAd" :key="sub.name" class="sub-feature-row">
                        <td class="sub-feature-label">{{ sub.name }}</td>
                        <!-- 免费版列 -->
                        <td class="sub-feature-value">
                          <svg class="check-icon" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </td>
                        <!-- 包年版各套餐列 -->
                        <td v-for="(val, idx) in sub.paidValues" :key="idx" class="sub-feature-value">
                          <template v-if="val">
                            <svg class="check-icon" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                          </template>
                          <template v-else>
                            <span class="empty"></span>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>

            <!-- 展开行：客服管理 -->
            <tr v-if="feature.name === '客服管理' && expandedRows.includes(index)" class="expand-row">
              <td :colspan="pricingTiers.length + 2" class="expand-cell">
                <div class="expand-content">
                  <table class="sub-pricing-table">
                    <tbody>
                      <tr v-for="sub in subFeaturesService" :key="sub.name" class="sub-feature-row">
                        <td class="sub-feature-label">{{ sub.name }}</td>
                        <!-- 免费版列 -->
                        <td class="sub-feature-value">
                          <svg class="check-icon" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </td>
                        <!-- 包年版各套餐列 -->
                        <td v-for="(val, idx) in sub.paidValues" :key="idx" class="sub-feature-value">
                          <template v-if="val">
                            <svg class="check-icon" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                          </template>
                          <template v-else>
                            <span class="empty"></span>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>

            <!-- 展开行：权限管理 -->
            <tr v-if="feature.name === '权限管理' && expandedRows.includes(index)" class="expand-row">
              <td :colspan="pricingTiers.length + 2" class="expand-cell">
                <div class="expand-content">
                  <table class="sub-pricing-table">
                    <tbody>
                      <tr v-for="sub in subFeaturesAuth" :key="sub.name" class="sub-feature-row">
                        <td class="sub-feature-label">{{ sub.name }}</td>
                        <!-- 免费版列 -->
                        <td class="sub-feature-value">
                          <svg class="check-icon" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </td>
                        <!-- 包年版各套餐列 -->
                        <td v-for="(val, idx) in sub.paidValues" :key="idx" class="sub-feature-value">
                          <template v-if="val">
                            <svg class="check-icon" viewBox="0 0 24 24">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                          </template>
                          <template v-else>
                            <span class="empty"></span>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- 常见问题模块 -->
    <div class="faq-section">
      <h2 class="faq-title">常见问题</h2>
      <div class="faq-list">
        <div class="faq-item">
          <div class="faq-question">Q1: 免费版和包年版的核心区别是什么？</div>
          <div class="faq-answer">
            免费版每月仅限5000单使用，仅提供线上直播培训，无售后退款保障；包年版无单量限制（按购买套餐），提供线上线下实施服务，支持60天无理由退款，且部分高级功能仅包年版可用。
          </div>
        </div>
        <div class="faq-item">
          <div class="faq-question">Q2: 订单量有效期如何计算？</div>
          <div class="faq-answer">
            免费版为自然月结算，每月重置5000单额度；包年版从签订合同之日起1年内有效，购买的订单量可在有效期内累计使用。
          </div>
        </div>
        <div class="faq-item">
          <div class="faq-question">Q3: 购买包年版后，超出订单量如何计费？</div>
          <div class="faq-answer">
            超出部分将按照对应套餐的单价进行计费（如12万单套餐超出后按0.083元/单计费），您也可以选择升级至更高档位的套餐以降低单均成本。
          </div>
        </div>
        <div class="faq-item">
          <div class="faq-question">Q4: 所有版本都支持多端使用吗？</div>
          <div class="faq-answer">
            是的，免费版和包年版均支持PC端、APP端使用，功能体验一致，仅在订单量和部分高级功能权限上有区别。
          </div>
        </div>
        <div class="faq-item">
          <div class="faq-question">Q5: 60天无理由退款的条件是什么？</div>
          <div class="faq-answer">
            包年版用户在购买后60天内，若使用订单量未超过购买总量的10%，可申请无理由退款，退款金额按未使用订单量比例计算。
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PriceComparison',
  
  data() {
    return {
      expandedRows: [],
      // 包年套餐顺序（索引对应：0=12万,1=30万,2=50万,3=70万,4=100万,5=130万,6=200万,7=280万,8=350万,9=600万,10=900万）
      pricingTiers: [
        { orders: '12万单', price: 10000, unitPrice: '0.083' },
        { orders: '30万单', price: 20000, unitPrice: '0.067' },
        { orders: '50万单', price: 30000, unitPrice: '0.060' },
        { orders: '70万单', price: 40000, unitPrice: '0.057' },
        { orders: '100万单', price: 50000, unitPrice: '0.050' },
        { orders: '130万单', price: 60000, unitPrice: '0.046' },
        { orders: '200万单', price: 80000, unitPrice: '0.040' },
        { orders: '280万单', price: 100000, unitPrice: '0.036' },
        { orders: '350万单', price: 120000, unitPrice: '0.034' },
        { orders: '600万单', price: 200000, unitPrice: '0.033' },
        { orders: '900万单', price: 300000, unitPrice: '0.033' }
      ],
      
      features: [
        { name: '实施服务', freeValue: '线上免费直播培训', paidValue: '线上线下免费实施服务', expandable: false },
        { name: '功能用量', freeValue: '支持所有功能', paidValue: '支持所有功能', expandable: true },
        { name: '运营工具', freeValue: true, paidValue: true, expandable: true },
        { name: '财务管理', freeValue: true, paidValue: true, expandable: true },
        { name: '进销存管理', freeValue: true, paidValue: true, expandable: true },
        { name: '数据管理', freeValue: true, paidValue: true, expandable: true },
        { name: '广告管理', freeValue: true, paidValue: true, expandable: true },
        { name: '客服管理', freeValue: true, paidValue: true, expandable: true },
        { name: '权限管理', freeValue: true, paidValue: true, expandable: true },
        { name: '数据看板', freeValue: '基础版', paidValue: '高级版', expandable: false },
        { name: '支持PC端、APP端', freeValue: true, paidValue: true, expandable: false },
        { name: '订单量有效期', freeValue: '每月5000单以内免费使用', paidValue: '签订合同起1年内', expandable: false },
        { name: '售后保障', freeValue: false, paidValue: '支持60天无理由退款', expandable: false }
      ],
      
      // 功能用量子功能
      subFeatures: [
        { name: "关键词监控", freeValue: "10", paidValues: ["500", "500", "2000", "2000", "2000", "2000", "2000", "2000", "2000", "2000", "2000"] },
        { name: "跟卖监控量", freeValue: "10", paidValues: ["50", "50", "200", "200", "200", "200", "200", "200", "200", "200", "200"] },
        { name: "上新监控", freeValue: "1", paidValues: ["10", "10", "20", "20", "20", "20", "20", "20", "20", "20", "20"] },
        { name: "关怀邮件", freeValue: "500", paidValues: ["50000", "50000", "50000", "80000", "80000", "80000", "12000", "150000", "150000", "150000", "150000"] },
        { name: "广告关键词同位", freeValue: "2", paidValues: ["10", "10", "20", "20", "20", "20", "30", "30", "40", "50", "50"] },
        { name: "站点", freeValue: "20", paidValues: ["100", "", "", "", "", "", "", "", "", "", ""] },
        { name: "竞品监控", freeValue: "1", paidValues: ["50", "", "", "", "", "", "", "", "", "", ""] },
        { name: "Listing数量", freeValue: "10000", paidValues: ["不限", "", "", "", "", "", "", "", "", "", ""] },
        { name: "客满邮件", freeValue: "不限", paidValues: ["不限", "", "", "", "", "", "", "", "", "", ""] }
      ],

      // 运营工具子功能（修正勾选逻辑：匹配截图的套餐对应关系）
      subFeaturesOperations: [
        { name: "预警消息", paidValues: [false, false, false, false, false, true, false, false, false, false, false] }, // 130万单
        { name: "Listing自动调价", paidValues: [false, false, false, false, false, false, true, false, false, false, false] }, // 200万单
        { name: "定价利润试算", paidValues: [false, false, false, false, false, false, false, true, false, false, false] }, // 280万单
        { name: "AI工具", paidValues: [false, false, false, false, false, false, false, false, true, false, false] } // 350万单
      ],

      // 财务管理子功能
      subFeaturesFinance: [
        { name: "利润报表",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "业绩报表",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "支持按月封存结账",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "可导入其他费用",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "收付款流程管理",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "回款记录",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "按批次核算成本",paidValues: [false, false, false, false, false, true, false, false, false, false, false] }
      ],

      // 进销存管理子功能
      subFeaturesInventory: [
        { name: "平台仓货件",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "智能补货建议",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "提前锁定组合产品的单品库存",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "采购和物流对账",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "采购管理",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "供应商绩效管理",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "采购合同模板",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "发货计划",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "物流管理",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "头程分摊" ,paidValues: [false, false, false, false, false, true, false, false, false, false, false]},
        { name: "自发货",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "境外长途发货调拨",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "仓库管理",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "海外仓备货",paidValues: [false, false, false, false, false, true, false, false, false, false, false] }
      ],

      // 数据管理子功能
      subFeaturesData: [
        { name: "产品表现报表",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "销量统计报表",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "库存报表",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "采购报表",paidValues: [false, false, false, false, false, true, false, false, false, false, false] }
      ],

      // 广告管理子功能
      subFeaturesAd: [
        { name: "分时策略",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "一键同步投放",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "广告报表",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "操作日志",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "跨广告类型投放",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "出单词库分析",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "商品反查",paidValues: [false, false, false, false, false, true, false, false, false, false, false] }
      ],

      // 客服管理子功能
      subFeaturesService: [
        { name: "群发售后关怀邮件",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "Request a review",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "趋势图展示",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "VAT发票管理",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "店铺绩效展示",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "邮件统计",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "评价统计",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "RMA管理",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "售后工单",paidValues: [false, false, false, false, false, true, false, false, false, false, false] }
      ],

      // 权限管理子功能
      subFeaturesAuth: [
        { name: "店铺权限",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "Listing权限",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "单据权限",paidValues: [false, false, false, false, false, true, false, false, false, false, false] },
        { name: "仓库权限",paidValues: [false, false, false, false, false, true, false, false, false, false, false] }
      ]
    }
  },

  methods: {
    toggleExpand(index) {
      const pos = this.expandedRows.indexOf(index)
      if (pos > -1) {
        this.expandedRows.splice(pos, 1)
      } else {
        this.expandedRows.push(index)
      }
    }
  }
}
</script>

<style scoped>
/* Layout */
.price-comparison {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
  background: #fff;
}

/* Header */
.header-section {
  text-align: center;
  margin-bottom: 4rem;
}

.main-title {
  font-size: 2rem;
  color: #1a1a2e;
  font-weight: 600;
  margin-bottom: 0.75rem;
  letter-spacing: 2px;
}

.subtitle {
  font-size: 0.9rem;
  color: #8b8b8b;
  font-weight: 400;
}

/* Table Container */
.pricing-table-container {
  overflow-x: auto;
  padding-bottom: 1rem;
  margin-bottom: 4rem;
}

.pricing-table {
  width: 100%;
  min-width: 1200px;
  border-collapse: collapse;
  border: none;
}

/* Table Header */
.header-row {
  background: #fff;
}

.corner-cell {
  width: 120px;
  border: none;
}

.plan-header {
  padding: 1.5rem;
  text-align: center;
  vertical-align: top;
  border: none;
  min-width: 150px;
}

.yearly-plan {
  background: #fff;
}

.plan-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 1rem;
}

.plan-title.highlighted {
  color: #2d5af5;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1.25rem;
  border-radius: 4px;
  font-size: 0.85rem;
  text-decoration: none;
  transition: all 0.2s;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn-outline {
  border-color: #d9d9d9;
  color: #666;
  background: #fff;
}

.btn-outline:hover {
  border-color: #2d5af5;
  color: #2d5af5;
}

.btn-primary {
  background: #2d5af5;
  color: #fff;
  border: none;
}

.btn-primary:hover {
  background: #1e4ad8;
}

/* Tier Row (第二行) */
.tier-row {
  border-bottom: 1px solid #f0f2f5;
  border-top: 1px solid #eaeffb;
}

.section-label {
  padding: 1rem;
  text-align: center;
  font-weight: 600;
  color: #1a1a2e;
  font-size: 0.9rem;
  border-right: 1px solid #f0f2f5;
  background: #fff;
}

.tier-cell {
  padding: 1rem 0.5rem;
  text-align: center;
  vertical-align: top;
  border-right: 1px solid #f0f2f5;
  min-width: 85px;
}

.tier-cell:last-child {
  border-right: none;
}

.free-tier {
  background: #fff;
}

.tier-quota {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
  white-space: nowrap;
}

.tier-quota.highlighted {
  color: #2d5af5;
}

.tier-price {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 0.25rem;
}

.tier-unit {
  font-size: 0.75rem;
  color: #8b8b8b;
  font-weight: 400;
}

/* Table Body */
.feature-row {
  border-bottom: 1px solid #f0f2f5;
  transition: background 0.2s;
}

.feature-row:hover {
  background: #f8f9ff;
}

.feature-label {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #1a1a2e;
  font-size: 0.9rem;
  border-right: 1px solid #f0f2f5;
  background: #fff;
  min-width: 120px;
}

.feature-name-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.expand-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.arrow {
  display: inline-block;
  transition: transform 0.2s;
  font-size: 1.2rem;
  color: #8b8b8b;
  transform: rotate(0deg);
}

.arrow.expanded {
  transform: rotate(90deg);
}

.feature-cell {
  padding: 1rem;
  text-align: center;
  vertical-align: middle;
  border-right: 1px solid #f0f2f5;
}

.free-cell {
  background: #fff;
  border-right: 1px solid #f0f2f5;
}

.paid-cell {
  border-right: none;
}

.check-icon {
  width: 18px;
  height: 18px;
  fill: #52c41a;
  display: inline-block;
  vertical-align: middle;
}

.text-value {
  font-size: 0.85rem;
  color: #1a1a2e;
  line-height: 1.4;
}

.empty {
  display: inline-block;
  width: 18px;
}

/* 展开内容 & 子表格样式 */
.expand-cell {
  padding: 0;
  background: #fafbfc;
}

.expand-content {
  padding: 1rem 1.5rem;
  border-top: 1px solid #f0f2f5;
}

.sub-pricing-table {
  width: 100%;
  border-collapse: collapse;
}

.sub-pricing-table th,
.sub-pricing-table td {
  padding: 0.75rem;
  text-align: center;
  border: 1px solid #f0f2f5;
}

.sub-feature-name {
  text-align: left;
  font-weight: 600;
  color: #1a1a2e;
  min-width: 120px;
}

.sub-free-col,
.sub-paid-col {
  font-weight: 600;
  color: #1a1a2e;
  min-width: 85px;
}

.sub-feature-row:hover {
  background: #f8f9ff;
}

.sub-feature-label {
  text-align: left;
  color: #1a1a2e;
  min-width: 120px;
}

.sub-feature-value {
  color: #1a1a2e;
  min-width: 85px;
}

/* 常见问题样式 */
.faq-section {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  background: #f8f9ff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.faq-title {
  font-size: 1.5rem;
  color: #1a1a2e;
  font-weight: 600;
  margin-bottom: 1.5rem;
  text-align: center;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.faq-item {
  background: #fff;
  padding: 1rem 1.5rem;
  border-radius: 4px;
  border-left: 3px solid #2d5af5;
}

.faq-question {
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.faq-answer {
  color: #666;
  font-size: 0.85rem;
  line-height: 1.6;
}

/* Responsive */
@media (max-width: 1024px) {
  .main-title {
    font-size: 1.5rem;
  }
  
  .pricing-table {
    min-width: 1000px;
  }
  
  .tier-cell {
    padding: 0.75rem 0.25rem;
    min-width: 70px;
  }
  
  .tier-quota {
    font-size: 0.85rem;
  }
  
  .tier-price {
    font-size: 0.9rem;
  }

  .faq-section {
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .price-comparison {
    padding: 2rem 1rem;
  }
  
  .pricing-table-container {
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;
  }

  .faq-question {
    font-size: 0.9rem;
  }

  .faq-answer {
    font-size: 0.8rem;
  }
}
</style>